import React from "react";
import {Alert, Button, LinearProgress, Typography} from "@mui/material";
import {UploadFileOutlined} from "@mui/icons-material";
import type {PtV2ImportError} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Classes.ts";
import {parseImportErrors} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Calc.ts";
import {usePtV2ImportMutation} from "~/experiments/pages/payment-terms-v2/usePaymentTermsV2Queries.ts";

type Result = {kind: 'ok'; sourceName: string} | {kind: 'invalid'; errors: PtV2ImportError[]} | {kind: 'failed'};

/** Staff only: upload a zip of Hermes exports for this databag (the fallback to the BigQuery sync). */
export const PtV2ImportPanel: React.FC = () => {
    const importM = usePtV2ImportMutation();
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [file, setFile] = React.useState<File | null>(null);
    const [result, setResult] = React.useState<Result | null>(null);

    const submit = async () => {
        if (!file) return;
        setResult(null);
        try {
            await importM.upload(file);
            setResult({kind: 'ok', sourceName: file.name});
            setFile(null);
            if (inputRef.current) inputRef.current.value = '';
        } catch (e) {
            const response = (e as {response?: {status?: number; data?: unknown}}).response;
            if (response?.status === 400) {
                const errors = parseImportErrors(response.data);
                setResult({kind: 'invalid', errors: errors.length ? errors : [{file: null, message: 'The export did not validate.'}]});
            } else {
                setResult({kind: 'failed'});
            }
        }
    };

    const byFile = new Map<string, PtV2ImportError[]>();
    if (result?.kind === 'invalid') {
        for (const e of result.errors) {
            const f = e.file ?? 'General';
            byFile.set(f, [...(byFile.get(f) ?? []), e]);
        }
    }

    return <div className="ptv2-import">
        <Typography variant="body2" className="ptv2-muted">
            Zip the Hermes exports as they come, named <code>&lt;slug&gt;_vN.xlsx</code>, <code>.csv</code>,{' '}
            <code>.json</code> or <code>.md</code>. No manifest is needed: the version comes from the file name, and
            every dataset is optional. Datasets in the zip replace the current version; unknown files are skipped.
            The taxonomy and the playbook only come in this way.
        </Typography>
        <div className="ptv2-import-row">
            <input ref={inputRef} type="file" accept=".zip,application/zip" hidden
                   onChange={e => {
                       setFile(e.target.files?.[0] ?? null);
                       setResult(null);
                   }}/>
            <Button variant="outlined" size="small" startIcon={<UploadFileOutlined/>}
                    onClick={() => inputRef.current?.click()} disabled={importM.isPending}>
                Choose zip
            </Button>
            <span className="ptv2-import-file">{file ? file.name : 'No file chosen'}</span>
            <Button variant="contained" size="small" disabled={!file || importM.isPending} onClick={() => void submit()}>
                Import
            </Button>
        </div>
        {importM.isPending && <LinearProgress/>}
        {result?.kind === 'ok' && <Alert severity="success">
            Imported {result.sourceName}. Every tab now shows the new data.
        </Alert>}
        {result?.kind === 'failed' && <Alert severity="error">The upload failed. Check your connection and try again.</Alert>}
        {result?.kind === 'invalid' && <Alert severity="error">
            <b>The export was not imported.</b> Fix these problems and export again:
            {[...byFile].map(([f, errs]) => <div key={f} className="ptv2-import-errors">
                <code>{f}</code>
                <ul>{errs.map((err, i) => <li key={i}>{err.message}</li>)}</ul>
            </div>)}
        </Alert>}
    </div>;
};
